import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Box,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  alpha,
  useTheme,
  Divider,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Category as CategoryIcon,
  SwapHoriz as SwapHorizIcon,
  ListAlt as ListAltIcon,
  QuestionAnswer as ConsultasIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as DisponibilidadesIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Straighten as StraightenIcon,
  VpnKey as VpnKeyIcon,
  AccountTree as AccountTreeIcon,
  UploadFile as UploadFileIcon,
  PriceChange as PriceChangeIcon,
  Schema as SchemaIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { canManageSistemaUsers, effectiveUserRoles } from '../utils/sistemaAuthz';
import { administrationMenuItem, isTesoreriaAdminHubPath } from '../config/adminNavigation';
import { clearAdminBackTo, navigateWithAdminBack } from '../utils/adminNavigationContext';

interface AccesoDirecto {
  text: string;
  icon: React.ReactNode;
  path: string;
  categoria: string;
  grupo: string;
}

interface BuscadorAccesosModalProps {
  open: boolean;
  onClose: () => void;
}

/** Normaliza texto para búsqueda sin acentos: "línea" y "linea" coinciden */
const normalizeForSearch = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const BuscadorAccesosModal: React.FC<BuscadorAccesosModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    'General': false,
    'Ventas': false,
    'Compras': false,
    'Inventario': false,
    'Tesorería': false,
    'Sistema': false,
    'Configuraciones': false,
    'Contabilidad': false
  });
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const todosLosAccesos: AccesoDirecto[] = [
    { text: 'Indicadores', icon: <DashboardIcon />, path: '/dashboard', categoria: 'General', grupo: 'Dashboard' },
    { text: 'Consulta IA Ventas', icon: <ConsultasIcon />, path: '/ventas/consultas-ia', categoria: 'Ventas', grupo: 'Consultas' },
    { text: 'Consulta IA Compras', icon: <ConsultasIcon />, path: '/compras/consultas-ia', categoria: 'Compras', grupo: 'Consultas' },
    { text: 'Consulta IA Inventario', icon: <ConsultasIcon />, path: '/inventario/consultas-ia', categoria: 'Inventario', grupo: 'Consultas' },
    { text: 'Consulta IA Tesorería', icon: <ConsultasIcon />, path: '/tesoreria/consultas-ia', categoria: 'Tesorería', grupo: 'Consultas' },

    // Ventas (orden igual que Sidebar: Cotizaciones, Pedidos, Facturas, Notas de Crédito, Informes, Clientes)
    { text: 'Cotizaciones', icon: <ListAltIcon />, path: '/ventas/cotizaciones', categoria: 'Ventas', grupo: 'Procesos' },
    { text: 'Pedidos', icon: <ListAltIcon />, path: '/ventas/pedidos', categoria: 'Ventas', grupo: 'Procesos' },
    { text: 'Facturas', icon: <ListAltIcon />, path: '/ventas/facturas', categoria: 'Ventas', grupo: 'Procesos' },
    { text: 'Remitos', icon: <ListAltIcon />, path: '/ventas/remitos', categoria: 'Ventas', grupo: 'Procesos' },
    { text: 'Notas de Crédito', icon: <ListAltIcon />, path: '/ventas/notas-credito', categoria: 'Ventas', grupo: 'Procesos' },
    { text: 'Planes de Visitas', icon: <ListAltIcon />, path: '/ventas/visitas?tab=planes', categoria: 'Ventas', grupo: 'Procesos' },
    { text: 'Visitas (Ejecución)', icon: <ListAltIcon />, path: '/ventas/visitas?tab=visitas', categoria: 'Ventas', grupo: 'Procesos' },
    { text: 'Informes de Ventas', icon: <AssessmentIcon />, path: '/ventas/informes', categoria: 'Ventas', grupo: 'Informes' },
    { text: 'Cuentas por Cobrar', icon: <AssessmentIcon />, path: '/ventas/informes/cuentas-cobrar', categoria: 'Ventas', grupo: 'Informes' },
    { text: 'Resumen de Cuenta', icon: <AssessmentIcon />, path: '/ventas/informes/resumen-cuenta', categoria: 'Ventas', grupo: 'Informes' },
    { text: 'Libro IVA Ventas', icon: <AssessmentIcon />, path: '/ventas/informes/libro-iva-ventas', categoria: 'Ventas', grupo: 'Informes' },
    { text: 'Informe dinámico de ventas', icon: <AssessmentIcon />, path: '/ventas/informes/ventas-dinamico', categoria: 'Ventas', grupo: 'Informes' },
    { text: 'Ventas por producto', icon: <AssessmentIcon />, path: '/ventas/informes/ventas-por-producto', categoria: 'Ventas', grupo: 'Informes' },
    { text: 'Clientes', icon: <CategoryIcon />, path: '/ventas/clientes', categoria: 'Ventas', grupo: 'Configuraciones' },
    { text: 'Vendedores', icon: <CategoryIcon />, path: '/ventas/vendedores', categoria: 'Ventas', grupo: 'Configuraciones' },
    { text: 'Rutas', icon: <CategoryIcon />, path: '/ventas/rutas', categoria: 'Ventas', grupo: 'Configuraciones' },
    { text: 'Listas de Precios', icon: <CategoryIcon />, path: '/ventas/listaprecios', categoria: 'Ventas', grupo: 'Configuraciones' },
    { text: 'Artículos por Lista de Precio', icon: <CategoryIcon />, path: '/ventas/articuloxlistaprecio', categoria: 'Ventas', grupo: 'Configuraciones' },
    { text: 'Cotizaciones de moneda', icon: <CategoryIcon />, path: '/sistema/movmoneda', categoria: 'Ventas', grupo: 'Configuraciones' },

    // Compras (orden igual que Sidebar: Órdenes de Compra, Remitos de Compra, Facturas de Compra, Informes, Proveedores)
    { text: 'Órdenes de Compra', icon: <ListAltIcon />, path: '/compras/ordenes', categoria: 'Compras', grupo: 'Procesos' },
    { text: 'Remitos de Compra', icon: <ListAltIcon />, path: '/compras/remitos', categoria: 'Compras', grupo: 'Procesos' },
    { text: 'Facturas de Compra', icon: <AssessmentIcon />, path: '/compras/facturas', categoria: 'Compras', grupo: 'Procesos' },
    { text: 'Informes de Compras', icon: <AssessmentIcon />, path: '/compras/informes', categoria: 'Compras', grupo: 'Informes' },
    { text: 'Cuentas por Pagar', icon: <AssessmentIcon />, path: '/compras/informes/cuentas-pagar', categoria: 'Compras', grupo: 'Informes' },
    { text: 'Resumen de Cuenta', icon: <AssessmentIcon />, path: '/compras/informes/resumen-cuenta', categoria: 'Compras', grupo: 'Informes' },
    { text: 'Libro IVA Compras', icon: <AssessmentIcon />, path: '/compras/informes/libro-iva-compras', categoria: 'Compras', grupo: 'Informes' },
    { text: 'Proveedores', icon: <CategoryIcon />, path: '/compras/proveedores', categoria: 'Compras', grupo: 'Configuraciones' },
    { text: 'Ramos de Proveedores', icon: <CategoryIcon />, path: '/compras/ramosprov', categoria: 'Compras', grupo: 'Configuraciones' },
    { text: 'Cotizaciones de moneda', icon: <CategoryIcon />, path: '/sistema/movmoneda', categoria: 'Compras', grupo: 'Configuraciones' },

    // Inventario (hub mantenimiento precios + importación, Informes, Productos, …)
    { text: 'Ajustes y Transferencias', icon: <SwapHorizIcon />, path: '/inventario/transferencias', categoria: 'Inventario', grupo: 'Procesos' },
    { text: 'Mantenimiento de precios', icon: <PriceChangeIcon />, path: '/inventario/mantenimiento-precios', categoria: 'Inventario', grupo: 'Procesos' },
    { text: 'Importación costos Base por código de proveedor', icon: <UploadFileIcon />, path: '/inventario/importacion-costos-proveedor', categoria: 'Inventario', grupo: 'Procesos' },
    { text: 'Importación costos Base por código de artículo', icon: <UploadFileIcon />, path: '/inventario/importacion-costos-articulo', categoria: 'Inventario', grupo: 'Procesos' },
    { text: 'Informes de Inventario', icon: <AssessmentIcon />, path: '/inventario/informes', categoria: 'Inventario', grupo: 'Informes' },
    { text: 'Stock por Depósito', icon: <AssessmentIcon />, path: '/inventario/informes/stock-deposito', categoria: 'Inventario', grupo: 'Informes' },
    { text: 'Movimientos de Stock', icon: <AssessmentIcon />, path: '/inventario/informes/movimientos-stock', categoria: 'Inventario', grupo: 'Informes' },
    { text: 'Productos', icon: <CategoryIcon />, path: '/inventario/productos', categoria: 'Inventario', grupo: 'Configuraciones' },
    { text: 'Ramos', icon: <CategoryIcon />, path: '/inventario/ramos', categoria: 'Inventario', grupo: 'Configuraciones' },
    { text: 'Líneas', icon: <CategoryIcon />, path: '/inventario/lineas', categoria: 'Inventario', grupo: 'Configuraciones' },
    { text: 'Clases', icon: <CategoryIcon />, path: '/inventario/clases', categoria: 'Inventario', grupo: 'Configuraciones' },
    { text: 'Unidades de medida', icon: <StraightenIcon />, path: '/inventario/unidades-medida', categoria: 'Inventario', grupo: 'Configuraciones' },

    // Tesorería (orden igual que Sidebar: Recibos, Orden de Pago, Traspasos, Movimientos Varios, Movimientos Bancarios, Conciliación, Planilla de Caja, Informes)
    { text: 'Recibos', icon: <ListAltIcon />, path: '/tesoreria/recibos', categoria: 'Tesorería', grupo: 'Procesos' },
    { text: 'Orden de Pago', icon: <ListAltIcon />, path: '/tesoreria/orden-pago', categoria: 'Tesorería', grupo: 'Procesos' },
    { text: 'Traspasos', icon: <SwapHorizIcon />, path: '/tesoreria/transferencias', categoria: 'Tesorería', grupo: 'Procesos' },
    { text: 'Movimientos Varios', icon: <ListAltIcon />, path: '/tesoreria/movimientos-varios', categoria: 'Tesorería', grupo: 'Procesos' },
    { text: 'Movimientos Bancarios', icon: <ListAltIcon />, path: '/tesoreria/movimientos-bancarios', categoria: 'Tesorería', grupo: 'Procesos' },
    { text: 'Conciliación Bancaria', icon: <ListAltIcon />, path: '/tesoreria/conciliacion-bancaria', categoria: 'Tesorería', grupo: 'Procesos' },
    { text: 'Planilla de Caja', icon: <ReceiptIcon />, path: '/tesoreria/movimientos-caja', categoria: 'Tesorería', grupo: 'Procesos' },
    { text: 'Informes de Tesorería', icon: <AssessmentIcon />, path: '/tesoreria/informes', categoria: 'Tesorería', grupo: 'Informes' },
    { text: 'Disponibilidades', icon: <DisponibilidadesIcon />, path: '/tesoreria/disponibilidades', categoria: 'Tesorería', grupo: 'Informes' },
    { text: 'Movimientos de Carteras', icon: <AssessmentIcon />, path: '/tesoreria/informes/movimientos-carteras', categoria: 'Tesorería', grupo: 'Informes' },
    { text: 'Cheques de Tercero', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-tercero', categoria: 'Tesorería', grupo: 'Informes' },
    { text: 'Cheques de Terceros en Cartera', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-terceros-cartera', categoria: 'Tesorería', grupo: 'Informes' },
    { text: 'Cheques Propios', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-propios', categoria: 'Tesorería', grupo: 'Informes' },
    { text: 'Cheques Propios Pendientes de Acreditar', icon: <AssessmentIcon />, path: '/tesoreria/informes/cheques-propios-pendientes-acreditar', categoria: 'Tesorería', grupo: 'Informes' },
    { text: 'Bancos', icon: <CategoryIcon />, path: '/tesoreria/bancos', categoria: 'Tesorería', grupo: 'Configuraciones' },
    { text: 'Carteras', icon: <CategoryIcon />, path: '/tesoreria/carteras', categoria: 'Tesorería', grupo: 'Configuraciones' },
    { text: 'Grupos de Caja', icon: <CategoryIcon />, path: '/tesoreria/grupos-mcp', categoria: 'Tesorería', grupo: 'Configuraciones' },
    { text: 'Medios MCP', icon: <CategoryIcon />, path: '/tesoreria/medios-mcp', categoria: 'Tesorería', grupo: 'Configuraciones' },
    { text: 'Tarjetas', icon: <CategoryIcon />, path: '/tesoreria/tarjetas', categoria: 'Tesorería', grupo: 'Configuraciones' },
    
    { text: 'Administración', icon: <SettingsIcon />, path: '/configuraciones', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Usuarios', icon: <CategoryIcon />, path: '/sistema/usuarios', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Correo para facturas', icon: <EmailIcon />, path: '/configuraciones/correo-salida', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Parámetros del sistema', icon: <SettingsIcon />, path: '/configuraciones/parametros', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Tipos de Formulario', icon: <CategoryIcon />, path: '/sistema/tipo-formulario', categoria: 'Sistema', grupo: 'Configuraciones' },
    { text: 'Tipos de formulario por Sucursal', icon: <CategoryIcon />, path: '/configuraciones/sucventa', categoria: 'Sistema', grupo: 'Configuraciones' },
    { text: 'Sucursales de Venta', icon: <CategoryIcon />, path: '/configuraciones/sucursalesventa', categoria: 'Sistema', grupo: 'Configuraciones' },
    { text: 'Compañías', icon: <CategoryIcon />, path: '/sistema/companias', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Administración de Tenants', icon: <CategoryIcon />, path: '/sistema/tenants', categoria: 'Sistema', grupo: 'Configuraciones' },
    { text: 'Caja por Usuario', icon: <CategoryIcon />, path: '/sistema/caja-usuario', categoria: 'Sistema', grupo: 'Configuraciones' },
    { text: 'Grupos de Caja por Usuario', icon: <CategoryIcon />, path: '/sistema/grupo-caja-usuario', categoria: 'Sistema', grupo: 'Configuraciones' },
    
    { text: 'Monedas', icon: <CategoryIcon />, path: '/sistema/monedas', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Cotizaciones de moneda', icon: <CategoryIcon />, path: '/sistema/movmoneda', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Provincias', icon: <CategoryIcon />, path: '/sistema/provincias', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Localidades', icon: <CategoryIcon />, path: '/sistema/localidades', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Transportes', icon: <CategoryIcon />, path: '/configuraciones/transportes', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Impuestos', icon: <CategoryIcon />, path: '/configuraciones/impuestos', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Ubicaciones', icon: <CategoryIcon />, path: '/configuraciones/ubicaciones', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    { text: 'Tokens de integración', icon: <VpnKeyIcon />, path: '/configuraciones/tokens-integracion', categoria: 'Configuraciones', grupo: 'Configuraciones' },
    
    { text: 'Asientos / Libro diario', icon: <ListAltIcon />, path: '/contabilidad/asientos-diario', categoria: 'Contabilidad', grupo: 'Operaciones' },
    { text: 'Libro mayor', icon: <AccountTreeIcon />, path: '/contabilidad/libro-mayor', categoria: 'Contabilidad', grupo: 'Operaciones' },
    { text: 'Plan de cuentas', icon: <SchemaIcon />, path: '/contabilidad/plan-cuentas', categoria: 'Contabilidad', grupo: 'Operaciones' },
    { text: 'Informes', icon: <AssessmentIcon />, path: '/contabilidad/informes', categoria: 'Contabilidad', grupo: 'Informes' },
    { text: 'Estado de resultados', icon: <AssessmentIcon />, path: '/contabilidad/estado-resultados', categoria: 'Contabilidad', grupo: 'Informes' },
    { text: 'Situación patrimonial', icon: <AssessmentIcon />, path: '/contabilidad/situacion-patrimonial', categoria: 'Contabilidad', grupo: 'Informes' },
    { text: 'Imputables', icon: <CategoryIcon />, path: '/contabilidad/imputables', categoria: 'Contabilidad', grupo: 'Configuraciones' },
    { text: 'No imputables', icon: <ListAltIcon />, path: '/contabilidad/noimputables', categoria: 'Contabilidad', grupo: 'Configuraciones' },
    { text: 'Estrategias', icon: <CategoryIcon />, path: '/contabilidad/estrategia', categoria: 'Contabilidad', grupo: 'Configuraciones' },
  ];

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      // Enfocar el input cuando se abre el modal
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        if (!open) {
          onClose();
        }
      }
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const accesosFiltrados = useMemo(() => {
    const roles = effectiveUserRoles(user?.roles);
    const isSuperAdmin = roles.some((r) => String(r).trim().toUpperCase() === 'SUPERADMIN');
    const canManageUsers = canManageSistemaUsers(roles);

    let accesos = todosLosAccesos;

    // Resto de Sistema (tipos de formulario, caja por usuario, etc.): solo SUPERADMIN
    if (!isSuperAdmin) {
      accesos = accesos.filter((acceso) => acceso.categoria !== 'Sistema');
    }

    // Configuraciones (incluye Usuarios para Admin y SUPERADMIN)
    if (!isSuperAdmin && !canManageUsers) {
      accesos = accesos.filter((acceso) => acceso.categoria !== 'Configuraciones');
    }

    // Visitas queda disponible solo para perfiles de gestión
    const normalizedRoles = (user?.roles || []).map(r => String(r).trim().toLowerCase());
    const canManageVisitas = normalizedRoles.some(r => ['admin', 'superadmin', 'gerente'].includes(r));
    if (!canManageVisitas) {
      accesos = accesos.filter(acceso => !acceso.path.startsWith('/ventas/visitas'));
    }
    
    if (!searchTerm.trim()) {
      return accesos;
    }

    const termino = normalizeForSearch(searchTerm.trim());
    return accesos.filter(acceso => 
      normalizeForSearch(acceso.text).includes(termino) ||
      normalizeForSearch(acceso.categoria).includes(termino) ||
      normalizeForSearch(acceso.grupo).includes(termino) ||
      normalizeForSearch(acceso.path).includes(termino)
    );
  }, [searchTerm, todosLosAccesos, user?.roles]);

  // Expandir automáticamente las categorías que tienen resultados al buscar
  useEffect(() => {
    if (searchTerm.trim()) {
      const categoriasConResultados = new Set(accesosFiltrados.map(acceso => acceso.categoria));
      setExpandedCategories(prev => {
        const newExpanded = { ...prev };
        ordenCategorias.forEach(categoria => {
          newExpanded[categoria] = categoriasConResultados.has(categoria);
        });
        return newExpanded;
      });
    } else {
      // Contraer todo cuando no hay búsqueda
      setExpandedCategories({
        'General': false,
        'Ventas': false,
        'Compras': false,
        'Inventario': false,
        'Tesorería': false,
        'Sistema': false,
        'Configuraciones': false,
        'Contabilidad': false
      });
    }
  }, [searchTerm]);

  const accesosPorCategoria = useMemo(() => {
    const grouped: Record<string, Record<string, AccesoDirecto[]>> = {};
    
    accesosFiltrados.forEach(acceso => {
      if (!grouped[acceso.categoria]) {
        grouped[acceso.categoria] = {};
      }
      if (!grouped[acceso.categoria][acceso.grupo]) {
        grouped[acceso.categoria][acceso.grupo] = [];
      }
      grouped[acceso.categoria][acceso.grupo].push(acceso);
    });

    return grouped;
  }, [accesosFiltrados]);

  const handleAccesoClick = (acceso: AccesoDirecto) => {
    const desdeAdministracion =
      acceso.grupo === 'Configuraciones' && acceso.categoria !== 'Tesorería';

    if (desdeAdministracion) {
      navigateWithAdminBack(navigate, acceso.path, administrationMenuItem.path);
    } else if (isTesoreriaAdminHubPath(acceso.path)) {
      clearAdminBackTo();
      navigate(acceso.path);
    } else {
      navigate(acceso.path);
    }
    onClose();
  };

  const toggleCategory = (categoria: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoria]: !prev[categoria]
    }));
  };

  const getGrupoColor = (grupo: string) => {
    switch (grupo) {
      case 'Procesos': return '#1976d2';
      case 'Operaciones': return '#1565c0';
      case 'Informes': return '#2e7d32';
      case 'Configuraciones': return '#ed6c02';
      case 'Dashboard': return '#9c27b0';
      case 'Consultas': return '#d32f2f';
      default: return '#757575';
    }
  };

  const ordenCategorias = ['General', 'Ventas', 'Compras', 'Inventario', 'Tesorería', 'Contabilidad', 'Configuraciones', 'Sistema'];
  const ordenGrupos = ['Dashboard', 'Procesos', 'Operaciones', 'Informes', 'Consultas', 'Configuraciones'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '85vh',
          backgroundColor: 'white'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ flex: 1 }} />
            <Chip
              label="Ctrl+K"
              size="small"
              sx={{
                backgroundColor: alpha('#00548f', 0.1),
                color: '#00548f',
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                ml: 1,
                color: '#00548f',
                '&:hover': {
                  backgroundColor: alpha('#00548f', 0.1),
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="Buscar accesos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#00548f' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: alpha('#00548f', 0.03),
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: alpha('#00548f', 0.2),
                },
                '&:hover fieldset': {
                  borderColor: '#00548f',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00548f',
                },
              },
            }}
          />

          {searchTerm && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {accesosFiltrados.length} resultado{accesosFiltrados.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </Box>

        <Box 
          sx={{ 
            px: 3, 
            pb: 3,
            maxHeight: 'calc(85vh - 180px)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha('#00548f', 0.05),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha('#00548f', 0.3),
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: alpha('#00548f', 0.5),
              },
            },
          }}
        >
          {ordenCategorias
            .filter(categoria => accesosPorCategoria[categoria])
            .map((categoria, catIndex) => (
              <Box key={categoria} sx={{ mb: catIndex < ordenCategorias.length - 1 ? 1.5 : 0 }}>
                <Box
                  onClick={() => toggleCategory(categoria)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    mb: 1.5,
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: alpha('#00548f', 0.05),
                    }
                  }}
                >
                  <Typography 
                    variant="overline" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#00548f',
                      fontSize: '0.85rem',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {categoria.toUpperCase()}
                  </Typography>
                  {expandedCategories[categoria] ? (
                    <ExpandLessIcon sx={{ color: '#00548f', fontSize: '1.2rem' }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: '#00548f', fontSize: '1.2rem' }} />
                  )}
                </Box>

                {expandedCategories[categoria] && (
                  <Box 
                    sx={{ 
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                      gap: 2
                    }}
                  >
                  {ordenGrupos
                    .filter(grupo => accesosPorCategoria[categoria][grupo])
                    .map(grupo => (
                      <Box 
                        key={grupo}
                        sx={{
                          backgroundColor: alpha('#00548f', 0.02),
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha('#00548f', 0.1),
                          p: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: alpha('#00548f', 0.3),
                            backgroundColor: alpha('#00548f', 0.04),
                          }
                        }}
                      >
                        <Chip 
                          label={grupo}
                          size="small"
                          sx={{ 
                            backgroundColor: alpha(getGrupoColor(grupo), 0.1),
                            color: getGrupoColor(grupo),
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            mb: 1.5,
                            height: '24px'
                          }}
                        />

                        <List disablePadding>
                          {accesosPorCategoria[categoria][grupo].map((acceso, index) => (
                            <ListItem 
                              key={acceso.path} 
                              disablePadding
                              sx={{ mb: index < accesosPorCategoria[categoria][grupo].length - 1 ? 0 : 0 }}
                            >
                              <ListItemButton
                                onClick={() => handleAccesoClick(acceso)}
                                sx={{
                                  borderRadius: 1,
                                  py: 0.25,
                                  px: 1,
                                  minHeight: 'auto',
                                  transition: 'all 0.15s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                    transform: 'translateX(4px)',
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={acceso.text}
                                  primaryTypographyProps={{
                                    fontSize: '0.75rem',
                                    fontWeight: 400,
                                    color: 'text.primary'
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ))}
                  </Box>
                )}

                {catIndex < ordenCategorias.filter(c => accesosPorCategoria[c]).length - 1 && (
                  <Divider sx={{ mt: 1.5, borderColor: alpha('#00548f', 0.1) }} />
                )}
              </Box>
            ))}

          {accesosFiltrados.length === 0 && (
            <Box 
              sx={{ 
                textAlign: 'center',
                py: 8
              }}
            >
              <SearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No se encontraron resultados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Intentá con otros términos de búsqueda
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BuscadorAccesosModal;
